import Loader from 'components/Loader';
import UploadMultipleImages from 'components/UploadMultipleImages';
import { getCities, getCountries, getRegions, getSubAreas } from 'containers/school/actions';
import { citiesSelector, countriesSelector, regionsSelector, subAreasSelector } from 'containers/school/selectors';
import { useFormik } from 'formik';
import { useRouter } from 'hooks/useRouter';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { createStructuredSelector } from 'reselect';
import { LATITUDE_REGEX, LONGITUDE_REGEX, MODULE_TYPE, PHONE_REGEX } from 'utils/constants';
import { checkIfValueExist } from 'utils/helper';
import { SMKLinks } from 'utils/links';
import * as Yup from 'yup';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import useRequest from '../../../hooks/useRequest';
import { HEATLHCARE_HEADERS } from '../helpers';

const stateSelector = createStructuredSelector({
  countries: countriesSelector,
  regions: regionsSelector,
  cities: citiesSelector,
  subAreas: subAreasSelector,
});

const UpdateHealthCareFacility = ({ isDisabled = false, details = null }) => {
  const { id: facilityId } = useParams();

  const router = useRouter();
  const history = useHistory();
  const dispatch = useDispatch();
  const { countries, regions, cities, subAreas } = useSelector(stateSelector);
  toast.configure();

  const [facilityDetails, setfacilitysDetails] = useState({});

  const [getFacilityRequest, { loading: getFacilityLoading }] = useRequest({
    ...HEATLHCARE_HEADERS,
    url: `/healthcare/healthcare-facility/admin/${facilityId}`,
    method: 'GET',
    onSuccess: (data) => {
      setfacilitysDetails(data);
    },
    onError: (err) => {
      toast.error(err?.message);
      console.log(err);
    },
  });

  const [addFacilityRequest, { loading: addFacilityLoading }] = useRequest({
    ...HEATLHCARE_HEADERS,
    url: `/healthcare/healthcare-facility/admin`,
    method: 'POST',
    onSuccess: () => {
      history.replace(SMKLinks.HEALTHCARE_MODULE_HEALTH_CARE_LISTING);
      toast.success('Success! Health care facility has been Added');
    },
    onError: (err) => {
      toast.error(err?.message);
    },
  });

  const [updateFacilityRequest, { loading: updateFacilityLoading }] = useRequest({
    ...HEATLHCARE_HEADERS,
    url: `/healthcare/healthcare-facility/admin/${facilityId}`,
    method: 'PUT',
    onSuccess: () => {
      window.location.reload();
      toast.success('Success! Health care facility has been updated.');
    },
    onError: (err) => {
      toast.error(err?.message);
      console.log(err);
    },
  });

  const formatObject = (val) => {
    if (isEmpty(val)) {
      return null;
    }
    return {
      label: val,
      value: val,
      name: val,
      slug: val,
    };
  };

  const editInitialValues = (data) => {
    const country = formatObject(data?.address?.country);
    const region = formatObject(data?.address?.region);
    const city = formatObject(data?.address?.city);
    const subArea = formatObject(data?.address?.subArea);

    if (!isEmpty(country)) {
      dispatch(
        getRegions.trigger({
          level: 1,
          country: country.name,
          page: 1,
          size: 500,
          sortBy: 'slug',
          sortOrder: 0,
        })
      );
    }
    if (!isEmpty(region)) {
      dispatch(
        getCities.trigger({
          level: 3,
          cityOnly: true,
          region: region.name,
          page: 1,
          size: 500,
          sortBy: 'slug',
          sortOrder: 0,
        })
      );
    }

    if (!isEmpty(city)) {
      dispatch(getSubAreas.trigger({ level: 4, city: city.name, page: 1, size: 500, sortBy: 'slug', sortOrder: 0 }));
    }

    return {
      id: data?.id,
      active: data?.active,
      deleted: data?.deleted,
      name: data?.name,
      email: data?.email ? data?.email : '',
      phoneNumber: data?.phoneNumber ? data?.phoneNumber : '',
      notes: data?.notes,
      images: !isEmpty(data?.images) ? data?.images : null,

      line1: data?.address?.line1,
      line2: data?.address?.line2,
      landmark: data?.address?.landmark,
      postalCode: data?.address?.postalCode,

      country,
      region,
      city,
      subArea,

      latitude: data?.address?.location?.latitude,
      longitude: data?.address?.location?.longitude,
    };
  };

  const getInitialValues = () => ({
    active: true,
    deleted: false,
    name: '',
    email: '',
    phoneNumber: '',
    notes: '',
    images: null,

    country: null,
    region: null,
    city: null,
    subArea: null,
    line1: '',
    line2: '',
    landmark: '',
    postalCode: '',
    latitude: '',
    longitude: '',
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: getInitialValues(),
    onSubmit: async (values) => {
      const payload = {
        active: values?.active,
        deleted: values?.deleted,
        name: values?.name,
        email: values?.email,
        phoneNumber: values?.phoneNumber,
        notes: values?.notes,
        images: !isEmpty(values.images) ? values.images : [],
        address: {
          line1: checkIfValueExist(values?.line1),
          line2: checkIfValueExist(values?.line2),
          country: !isEmpty(values?.country?.slug) ? values?.country?.slug : '',
          region: !isEmpty(values?.region?.slug) ? values?.region?.slug : '',
          city: !isEmpty(values?.city?.slug) ? values?.city?.slug : '',
          subArea: !isEmpty(values?.subArea?.slug) ? values?.subArea?.slug : '',
          landmark: checkIfValueExist(values?.landmark),
          postalCode: checkIfValueExist(values?.postalCode),
          location: {
            latitude: +values?.latitude,
            longitude: +values?.longitude,
          },
        },
      };

      isEmpty(facilityId)
        ? addFacilityRequest({
            body: payload,
          })
        : updateFacilityRequest({
            body: payload,
          });
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .required('Please provide name')
        .min(3, 'Title should be at least 3 characters long')
        .max(50, 'Title should be at most 50 characters long'),
      // email: Yup.string()
      //   .email()
      //   .required('Please provide email'),
      // phoneNumber: Yup.string()
      //   .required('Please provide phone number')
      //   .matches(PHONE_REGEX, 'Please enter correct format'),

      line1: Yup.string().required('Please provide Line 1'),
      postalCode: Yup.string().required('Please provide postal code'),
      country: Yup.object()
        .nullable(true)
        .test('countryTest', 'Please select country', (val) => !isEmpty(val)),
      region: Yup.object()
        .nullable(true)
        .test('regionTest', 'Please select region/state', (val) => !isEmpty(val)),
      city: Yup.object()
        .nullable(true)
        .test('cityTest', 'Please select city', (val) => !isEmpty(val)),
      latitude: Yup.string()
        .required('Please provide latitude')
        .matches(LATITUDE_REGEX, 'Please enter correct format'),
      longitude: Yup.string()
        .required('Please provide longitude')
        .matches(LONGITUDE_REGEX, 'Please enter correct format'),
    }),
  });

  useEffect(() => {
    if (!isEmpty(facilityId)) {
      getFacilityRequest();
    }
  }, [facilityId]);

  useEffect(() => {
    dispatch(getCountries.trigger({ level: 0, page: 1, size: 500, sortBy: 'slug', sortOrder: 0 }));
  }, []);

  useEffect(() => {
    if (!isEmpty(facilityDetails)) {
      const updatedValues = editInitialValues(facilityDetails);
      formik.setValues(updatedValues);
    }
  }, [facilityDetails]);

  const onCountrySelect = (country) => {
    if (!isEmpty(country)) {
      dispatch(
        getRegions.trigger({
          level: 1,
          country: country.name,
          page: 1,
          size: 500,
          sortBy: 'slug',
          sortOrder: 0,
        })
      );
      formik.setFieldValue('country', country);
      formik.setFieldValue('city', null);
      formik.setFieldValue('subArea', null);
    }
  };

  const onRegionSelect = (region) => {
    if (!isEmpty(region)) {
      dispatch(
        getCities.trigger({
          level: 3,
          cityOnly: true,
          region: region.name,
          page: 1,
          size: 500,
          sortBy: 'slug',
          sortOrder: 0,
        })
      );
      formik.setFieldValue('region', region);
      formik.setFieldValue('city', null);
      formik.setFieldValue('subArea', null);
    }
  };

  const onCitySelect = (city) => {
    if (!isEmpty(city)) {
      dispatch(getSubAreas.trigger({ level: 4, city: city.name, page: 1, size: 500, sortBy: 'slug', sortOrder: 0 }));

      formik.setFieldValue('city', city);
      formik.setFieldValue('subArea', null);
    }
  };

  const onSubAreaSelect = (subArea) => {
    if (!isEmpty(subArea)) {
      if (!subArea.id && !subArea.name) {
        subArea.name = subArea.label;
      }

      formik.setFieldValue('subArea', subArea);
    }
  };

  useEffect(() => {
    if (!isEmpty(details)) {
      const updatedValues = editInitialValues(details);
      formik.setValues(updatedValues);
    }
  }, [details]);

  return (
    <>
      <div className={!isDisabled ? 'page-content' : ''}>
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Loader isActive={addFacilityLoading || getFacilityLoading || updateFacilityLoading} />
          {!isDisabled && (
            <Breadcrumbs
              title="Health care facility"
              breadcrumbItem={`${isEmpty(facilityId) ? 'Add' : 'Edit'} Health care facility`}
            />
          )}
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={isDisabled ? 12 : 9}>
                <Card>
                  <CardBody>
                    <Row>
                      <Col md={12}>
                        <FormGroup className="mb-4">
                          <Label for="name" className="form-label  ">
                            Name
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="name"
                            type="text"
                            className="form-control"
                            placeholder="Name..."
                            invalid={!!(formik.touched.name && formik.errors.name)}
                            {...formik.getFieldProps('name')}
                            error={!isEmpty(formik.errors.name)}
                          />
                          <FormFeedback className="d-block">{formik.errors.name}</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="email" className="form-label  ">
                            Email
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="email"
                            type="text"
                            className="form-control"
                            placeholder="Email..."
                            invalid={!!(formik.touched.email && formik.errors.email)}
                            {...formik.getFieldProps('email')}
                          />
                          <FormFeedback className="d-block">{formik.errors.email}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="phoneNumber" className="form-label  ">
                            Phone Number
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="phoneNumber"
                            type="number"
                            className="form-control"
                            placeholder="Phone Number..."
                            invalid={!!(formik.touched.phoneNumber && formik.errors.phoneNumber)}
                            {...formik.getFieldProps('phoneNumber')}
                          />
                          <FormFeedback className="d-block">{formik.errors.phoneNumber}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup className="mb-4">
                          <Label for="schooldesc" className="form-label  ">
                            Notes
                          </Label>
                          <Input
                            disabled={isDisabled}
                            type="textarea"
                            className="form-control"
                            id="schooldesc"
                            rows="8"
                            placeholder="Notes..."
                            invalid={!!(formik.touched.notes && formik.errors.notes)}
                            {...formik.getFieldProps('notes')}
                          />
                          <FormFeedback className="d-block">{formik.errors.notes}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <h5 className="mb-4 pb-3 border-bottom">Address</h5>
                    <Row>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="country" className="form-label">
                            Country
                          </Label>
                          <Select
                            isDisabled={isDisabled}
                            id="country"
                            options={countries}
                            value={formik.values.country}
                            placeholder="Please select a country"
                            onChange={onCountrySelect}
                          />
                          {formik.touched.country && (
                            <div className="invalid-feedback d-block">{formik.errors.country}</div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="region" className="form-label  ">
                            Region
                          </Label>
                          <Creatable
                            isDisabled={isDisabled}
                            id="region"
                            placeholder="Select region..."
                            value={formik.values.region}
                            onChange={onRegionSelect}
                            options={regions}
                          />
                          {formik.touched.city && (
                            <div className="invalid-feedback d-block">{formik.errors.region}</div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="city" className="form-label  ">
                            City
                          </Label>
                          <Creatable
                            isDisabled={isDisabled}
                            id="city"
                            placeholder="Select city..."
                            value={formik.values.city}
                            onChange={onCitySelect}
                            options={cities}
                          />
                          {formik.touched.city && <div className="invalid-feedback d-block">{formik.errors.city}</div>}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="subArea" className="form-label  ">
                            SubArea
                          </Label>
                          <Select
                            isDisabled={isDisabled}
                            id="subArea"
                            placeholder="Select subarea..."
                            value={formik.values.subArea}
                            onChange={onSubAreaSelect}
                            options={subAreas}
                          />
                          {formik.touched.subArea && (
                            <div className="invalid-feedback d-block">{formik.errors.subArea}</div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="line1" className="form-label  ">
                            Line 1
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="line1"
                            type="text"
                            className="form-control"
                            placeholder="Line 1..."
                            invalid={!!(formik.touched.line1 && formik.errors.line1)}
                            {...formik.getFieldProps('line1')}
                          />
                          <FormFeedback className="d-block">{formik.errors.line1}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="line2" className="form-label  ">
                            Line 2
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="line2"
                            type="text"
                            className="form-control"
                            placeholder="Line 2..."
                            invalid={!!(formik.touched.line2 && formik.errors.line2)}
                            {...formik.getFieldProps('line2')}
                          />
                          <FormFeedback className="d-block">{formik.errors.line2}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="landmark" className="form-label  ">
                            Landmark
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="landmark"
                            type="text"
                            className="form-control"
                            placeholder="Landmark..."
                            invalid={!!(formik.touched.landmark && formik.errors.landmark)}
                            {...formik.getFieldProps('landmark')}
                          />
                          <FormFeedback className="d-block">{formik.errors.landmark}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="postalCode" className="form-label  ">
                            Postal code
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="postalCode"
                            type="number"
                            className="form-control"
                            placeholder="Postal code..."
                            invalid={!!(formik.touched.postalCode && formik.errors.postalCode)}
                            {...formik.getFieldProps('postalCode')}
                          />
                          <FormFeedback className="d-block">{formik.errors.postalCode}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label htmlFor="latitude" className="form-label  ">
                            Latitude
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="latitude"
                            type="text"
                            className="form-control mb-1"
                            placeholder="Latitude..."
                            invalid={!!(formik.touched.latitude && formik.errors.latitude)}
                            {...formik.getFieldProps('latitude')}
                          />
                          Ref: 20.12345678
                          <FormFeedback className="d-block">{formik.errors.latitude}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label htmlFor="longitude" className="form-label  ">
                            Longitude
                          </Label>
                          <Input
                            disabled={isDisabled}
                            id="longitude"
                            type="text"
                            className="form-control mb-1"
                            placeholder="Longitude..."
                            invalid={!!(formik.touched.longitude && formik.errors.longitude)}
                            {...formik.getFieldProps('longitude')}
                          />
                          Ref: 20.12345678
                          <FormFeedback className="d-block">{formik.errors.longitude}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {!isDisabled && (
                <Col md={3}>
                  <Card>
                    <CardBody>
                      <Button type="submit" color="primary" className="w-100 mb-3">
                        {`${isEmpty(facilityId) ? 'Add' : 'Update'}`} Health care facility
                      </Button>

                      <Button
                        className="w-100"
                        color="light"
                        onClick={() => {
                          history.replace(SMKLinks.HEALTHCARE_MODULE_HEALTH_CARE_LISTING);
                        }}>
                        Cancel
                      </Button>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <div>
                        <CardTitle className="mb-3">Active</CardTitle>
                        {[true, false].map((option, i) => (
                          <div key={`active_${i}`} className="form-check mb-3 d-inline-block me-4">
                            <input
                              type="radio"
                              id={`active_${option}`}
                              name="active"
                              className="form-check-input"
                              checked={formik.values.active === option}
                              onChange={() => formik.setFieldValue('active', option)}
                            />
                            <label className="form-check-label" htmlFor={`active_${option}`}>
                              {option ? 'Yes' : 'No'}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div>
                        <CardTitle className="mb-3">Delete </CardTitle>
                        {[true, false].map((option, i) => (
                          <div key={`deleted_${i}`} className="form-check mb-3 d-inline-block me-4">
                            <input
                              type="radio"
                              id={`deleted_${option}`}
                              name="deleted"
                              className="form-check-input"
                              checked={formik.values.deleted === option}
                              onChange={() => formik.setFieldValue('deleted', option)}
                            />
                            <label className="form-check-label" htmlFor={`deleted_${option}`}>
                              {option ? 'Yes' : 'No'}
                            </label>
                          </div>
                        ))}
                      </div>
                    </CardBody>
                  </Card>

                  <UploadMultipleImages
                    forType={MODULE_TYPE.METADATA}
                    formik={formik}
                    fieldName={'images'}
                    label={'Select Image'}
                    isHealthcare={true}
                  />
                </Col>
              )}
            </Row>
          </Form>
        </Container>
      </div>
    </>
  );
};

export default UpdateHealthCareFacility;
